import { useEffect, useRef, useState } from "react";
import { Trash2, Upload, Image as ImageIcon } from "lucide-react";
import Button from "../Button";
import ConfirmDialog from "../../modal/ConfirmDialog";
import { useToast } from "../../../_context/ToastContext/ToastContext";
import "./_image-upload.scss";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const VALID_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/svg+xml", "image/gif"];

/**
 * Rectangular-preview image upload + remove-with-confirmation - replaces
 * `@simplifyhq/simplify-ui-reusables`'s `FormInputImageUpload`. Adapts the
 * already-proven `AvatarUpload` (same file validation, same
 * remove-with-`ConfirmDialog` flow) but with a plain rectangular preview
 * instead of `AvatarUpload`'s circular `Avatar`-component preview, and
 * keeps the OLD component's prop names (`onUpload`/`existingImage`/
 * `setShouldDeleteImage` - a raw state setter, not a callback, matching
 * `renderInput.jsx`'s existing call site exactly) rather than
 * `AvatarUpload`'s own (`onSelectFile`/`src`/`onRemove`).
 */
const ImageUpload = ({ existingImage, onUpload, setShouldDeleteImage, disabled = false, isUploading = false }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  // `existingImage` is only ever a real, already-uploaded URL - the caller
  // doesn't know the actual URL for a just-picked file until it's uploaded
  // at submit time, so without a local preview the box shows nothing
  // between picking a file and the form actually submitting. Generated
  // from the File itself, entirely local to this component.
  const [localPreviewUrl, setLocalPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);
  const { showToast } = useToast();

  // A newly-loaded existingImage (e.g. switching which record is being
  // edited) makes any previous local preview stale - drop it so the real
  // value shows instead of a leftover blob: URL from a different file.
  useEffect(() => {
    setLocalPreviewUrl(null);
  }, [existingImage]);

  // Object URLs aren't garbage-collected automatically - revoke the
  // previous one whenever it's replaced or this unmounts.
  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    if (!VALID_TYPES.includes(file.type)) {
      showToast("Invalid file type. Please upload a PNG, JPG, SVG, or GIF image.", "error");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showToast("Image file is too large. Maximum size is 2MB.", "error");
      return;
    }

    setLocalPreviewUrl(URL.createObjectURL(file));
    onUpload?.(file);
  };

  const handleConfirmRemove = () => {
    setShowConfirm(false);
    setLocalPreviewUrl(null);
    setShouldDeleteImage?.(true);
  };

  const previewSrc = localPreviewUrl || existingImage;

  return (
    <div className="image-upload">
      <div className="image-upload__preview">
        {previewSrc ? (
          <img src={previewSrc} alt="" className="image-upload__image" />
        ) : (
          <ImageIcon size={24} className="image-upload__placeholder-icon" />
        )}
      </div>

      <div className="image-upload__controls">
        <Button
          type="button"
          variant="outlined"
          size="sm"
          icon={<Upload size={14} />}
          disabled={disabled || isUploading}
          loading={isUploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {previewSrc ? "Change image" : "Upload image"}
        </Button>
        {previewSrc && (
          <Button
            type="button"
            variant="ghost-danger"
            size="sm"
            iconOnly
            icon={<Trash2 size={14} />}
            disabled={disabled}
            onClick={() => setShowConfirm(true)}
            aria-label="Remove image"
          />
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="image-upload__file-input"
          onChange={handleFileChange}
          disabled={disabled}
        />
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        title="Remove this image?"
        description="This will remove the current image. You can upload a new one anytime."
        confirmText="Yes, remove"
        cancelText="Cancel"
        onCancel={() => setShowConfirm(false)}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
};

export default ImageUpload;
